import {createEvent, createStore, combine, Event} from '@effector/effector'

import {IS_IOS, TIMELINE_MAX, MD} from '../env'
import {runRuntime} from '../workingSet'
import {stores, events, effects} from '../runtime/units'
type TimelineEntry = {
  id: number
  time: number
  name: string
  value: any
}
let nextEntryID = 0
const addTimelineEntry = createEvent<TimelineEntry>()
const inputAddTimelineEntry: Event<{
  time: number
  name: string
  value: any
}> = addTimelineEntry.prepend(({time, name, value}) => {
  return {
    id: ++nextEntryID,
    time,
    name,
    value
  }
})
export {inputAddTimelineEntry as addTimelineEntry}

const TIME_ERROR = 5
const TIMELINE_DELTA_MAX = 15e3
const TIMELINE_LOOKAHEAD = 2e3
export let VISIBLE_SIZE = 150
const ROW_SIZE = MD * 2
const ENTRY_COMPENSATION_OFFSET_Y_SKIP_HEADER = MD * 3
const ENTRY_COMPENSATION_OFFSET_Y_POINT = MD
const ENTRY_COMPENSATION_OFFSET_Y_TEXT = IS_IOS ? 5 : 0
const ENTRY_COMPENSATION_OFFSET_Y =
  ENTRY_COMPENSATION_OFFSET_Y_TEXT + ENTRY_COMPENSATION_OFFSET_Y_SKIP_HEADER
const timelineCleanup = createEvent()
const timelineStart = createStore(performance.now())
  .on(runRuntime, () => performance.now() - TIME_ERROR)
  .on(timelineCleanup, () => performance.now() - TIME_ERROR - TIMELINE_MAX)
const timelineSnapStart = createStore(performance.now())
  .on(runRuntime, () => performance.now() - TIME_ERROR)
  .on(addTimelineEntry, (current, {time}) => {
    return time - TIMELINE_DELTA_MAX
  })
const timelineSnapEnd = createStore(performance.now() + TIMELINE_LOOKAHEAD)
  .on(runRuntime, () => performance.now() + TIMELINE_LOOKAHEAD)
  .on(addTimelineEntry, (current, {time}) => {
    return time + TIMELINE_LOOKAHEAD
  })
export const timelineItems = createStore<TimelineEntry[]>([])
  .on(addTimelineEntry, (list, entry) => [...list, entry])
  .on(timelineCleanup, list => {
    const min = performance.now() - TIMELINE_MAX
    return list.filter(({time}) => time > min)
  })
  .reset(runRuntime)

export const timelineRows = combine(
  stores,
  events,
  effects,
  (stores, events, effects) => [
    ...stores.map(({name}) => name),
    ...events.map(({name}) => name),
    ...effects.map(({name}) => name)
  ]
)

createStore(setInterval(timelineCleanup, TIMELINE_MAX)).on(
  runRuntime,
  timer => {
    clearInterval(timer)
    return setInterval(timelineCleanup, TIMELINE_MAX)
  }
)
export const getOffset = combine(
  timelineStart,
  timelineSnapStart,
  timelineSnapEnd,
  timelineRows,
  (start, snapStart, end, rows) => {
    const snapSize = end - snapStart
    const ms = VISIBLE_SIZE / snapSize
    return ({time, name}: TimelineEntry) => {
      const dt = time - start
      const x = dt * ms
      // console.log('rows, name', rows, name)
      const y = rows.indexOf(name) * ROW_SIZE + ENTRY_COMPENSATION_OFFSET_Y
      return {x, y}
    }
  }
)
export const timelineWidth = combine(
  timelineItems,
  getOffset,
  (items, getOffset) => {
    if (items.length < 2) return 1
    const first = getOffset(items[0])
    const last = getOffset(items[items.length - 1])
    return Math.ceil(last.x - first.x)
  }
)
export const timelineLookaheadOffset = combine(
  timelineStart,
  timelineSnapStart,
  timelineSnapEnd,
  (start, snapStart, snapEnd) => {
    const snapSize = snapEnd - snapStart
    const ms = VISIBLE_SIZE / snapSize
    const offsetSize = snapEnd - start + TIMELINE_LOOKAHEAD
    return Math.max((offsetSize * ms) | 0, 0)
  }
)
const getOffsetLegend = combine(
  timelineStart,
  timelineSnapStart,
  timelineSnapEnd,
  (start, snapStart, end) => {
    const snapSize = end - snapStart
    const ms = VISIBLE_SIZE / snapSize
    return (absTime: number) => {
      const dt = absTime - start
      const x = dt * ms
      return x
    }
  }
)
export const timelineLegend = combine(
  timelineStart,
  timelineSnapEnd,
  getOffsetLegend,
  (start, end, getOffset) => {
    const STEP = 1000
    let index = 1
    let current = start + STEP
    let results = ''
    while (current + STEP < end) {
      if (index % 5 === 0) {
        const offset = getOffset(current)
        results += `M ${offset} 7 V 18 `
      }
      current += STEP
      index += 1
    }
    return results
  }
)
export const timelineLegendSmall = combine(
  timelineStart,
  timelineSnapEnd,
  getOffsetLegend,
  (start, end, getOffset) => {
    const STEP = 1000
    let index = 1
    let current = start + STEP
    let results = ''
    while (current + STEP < end) {
      if (index % 5 !== 0) {
        const offset = getOffset(current)
        results += `M ${offset} 10 L ${offset} 15 `
      }
      current += STEP
      index += 1
    }
    return results
  }
)
